import { useEffect, useMemo, useState } from "react";
import { ArrowRightLeft, Ban, Mail, MapPin, UserMinus, Users } from "lucide-react";
import { useAdminEventDetails, useAdminEventTables, useBatchReassignTable } from "../api";
import {
  useSendUserEmails,
  EmailTemplate,
} from "../api/useAdminEmails";
import { useToast } from "@/components/ux/Toast";
import { useConfirm } from "@/components/ConfirmModal";

/**
 * Batch variant of ReassignmentDialog: moves EVERY user on a table to another
 * table, unassigns them all, or marks them all can't be seated (refund +
 * one apology broadcast), via `/admin/tables/:id/reassign-all`.
 */
interface TableReassignDialogProps {
  isOpen: boolean;
  /** Source table whose users get moved/unassigned. */
  tableId: string | null;
  eventTimeId: string | undefined;
  onClose: () => void;
  /** Fired after a successful change (admin-event caches are already invalidated). */
  onSuccess?: () => void;
}

type Choice =
  | { kind: "table"; tableId: string }
  | { kind: "unassign" }
  | { kind: "cant_be_seated" }
  | null;

export function TableReassignDialog({
  isOpen,
  tableId,
  eventTimeId,
  onClose,
  onSuccess,
}: TableReassignDialogProps) {
  const toast = useToast();
  const { confirm } = useConfirm();
  const batchReassign = useBatchReassignTable(eventTimeId);
  const sendUserEmails = useSendUserEmails();

  const { data: eventData, isLoading: eventLoading } =
    useAdminEventDetails(isOpen ? eventTimeId : undefined);
  const { data: tablesData, isLoading: tablesLoading } =
    useAdminEventTables(isOpen ? eventTimeId : undefined);

  const [choice, setChoice] = useState<Choice>(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (isOpen) {
      setChoice(null);
      setFilter("");
    }
  }, [isOpen, tableId]);

  const tables = useMemo(() => {
    const tablesPayload = tablesData?.data as { tables?: any[]; assignments?: any[] } | undefined;
    const tableList = tablesPayload?.tables || (eventData?.data?.tables as any[]) || [];
    const assignments = tablesPayload?.assignments || (eventData?.data?.assignments as any[]) || [];
    const counts = new Map<string, number>();
    assignments.forEach((a: any) => {
      counts.set(a.availableTableId, (counts.get(a.availableTableId) || 0) + 1);
    });
    return tableList.map((t: any) => ({
      id: t.id as string,
      reservationName: t.reservationName as string,
      capacity: t.capacity as number,
      isLocked: t.state === "locked",
      restaurantName: t.restaurant?.name as string | undefined,
      restaurantNeighborhoodName: t.restaurant?.neighborhoodName as string | undefined,
      currentCount: counts.get(t.id) || 0,
    }));
  }, [tablesData, eventData]);

  const sourceTable = tableId ? tables.find((t) => t.id === tableId) : undefined;
  const sourceCount = sourceTable?.currentCount ?? 0;
  const sourceNeighborhood = sourceTable?.restaurantNeighborhoodName;

  const sortedTables = useMemo(() => {
    const srcN = sourceNeighborhood?.toLowerCase();
    return [...tables]
      .filter((t) => t.id !== tableId)
      .sort((a, b) => {
        const aN = a.restaurantNeighborhoodName?.toLowerCase() || "";
        const bN = b.restaurantNeighborhoodName?.toLowerCase() || "";
        const aIsSrc = srcN && aN === srcN;
        const bIsSrc = srcN && bN === srcN;
        if (aIsSrc && !bIsSrc) return -1;
        if (!aIsSrc && bIsSrc) return 1;
        const nCompare = aN.localeCompare(bN);
        if (nCompare !== 0) return nCompare;
        return (a.restaurantName || "").localeCompare(b.restaurantName || "");
      });
  }, [tables, tableId, sourceNeighborhood]);

  const filteredTables = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return sortedTables;
    return sortedTables.filter(
      (t) =>
        t.reservationName?.toLowerCase().includes(q) ||
        t.restaurantName?.toLowerCase().includes(q) ||
        t.restaurantNeighborhoodName?.toLowerCase().includes(q)
    );
  }, [sortedTables, filter]);

  const isLoading = eventLoading || tablesLoading;

  const handleSubmit = async () => {
    if (!tableId || !choice || sourceCount === 0) return;

    // Mass refund + apology email is irreversible: double-check first.
    if (choice.kind === "cant_be_seated") {
      const confirmed = await confirm({
        title: `Mark all ${sourceCount} user(s) can't be seated?`,
        message:
          `Every guest on ${sourceTable?.reservationName ?? "this table"} will be ` +
          `removed from the dinner, refunded their ticket, and sent the apology email. ` +
          `This can't be undone.`,
        confirmText: `Mark ${sourceCount} can't be seated`,
        cancelText: "Cancel",
      });
      if (!confirmed) return;
    }

    const action =
      choice.kind === "table"
        ? ({ action: "assign" as const, tableId: choice.tableId })
        : choice.kind === "unassign"
          ? ({ action: "unassign" as const })
          : ({ action: "cant_be_seated" as const });

    batchReassign.mutate(
      { tableId, ...action },
      {
        onSuccess: (data) => {
          if (data?.success === false || !data?.data) {
            toast.error(data?.error || "Failed to reassign table");
            return;
          }
          const { changed, changedUserIds, failed, emailSent } = data.data;
          if (failed.length > 0) {
            toast.error(
              choice.kind === "cant_be_seated"
                ? `${changed} marked can't be seated, ${failed.length} failed — check the table and retry`
                : `${changed} moved, ${failed.length} failed — check the table and retry`
            );
          } else if (changed === 0) {
            toast.success("No changes");
          } else if (choice.kind === "cant_be_seated") {
            toast.success(
              emailSent
                ? `${changed} user(s) marked can't be seated · apology emails sent`
                : `${changed} user(s) marked can't be seated`
            );
          } else if (choice.kind === "unassign") {
            toast.success(`${changed} user(s) unassigned`);
          } else {
            const target = tables.find((t) => t.id === choice.tableId);
            toast.success(
              `${changed} user(s) moved to ${target?.reservationName ?? "table"}`
            );
          }
          onSuccess?.();
          onClose();

          // Same email logic as the single-user dialog, but one prompt for
          // the whole group: users landing on a locked table need to be told
          // where to go (details) or that their table changed (updated).
          const target =
            choice.kind === "table"
              ? tables.find((t) => t.id === choice.tableId)
              : undefined;
          if (
            target?.isLocked &&
            eventTimeId &&
            changed > 0 &&
            changedUserIds.length > 0
          ) {
            const movedBetweenLockedTables = !!sourceTable?.isLocked;
            const template = movedBetweenLockedTables
              ? EmailTemplate.BOOKING_UPDATED
              : EmailTemplate.BOOKING_DETAILS;
            void (async () => {
              const confirmed = await confirm({
                title: movedBetweenLockedTables
                  ? "Send updated booking emails?"
                  : "Send assignment emails?",
                message: movedBetweenLockedTables
                  ? `${changed} user(s) already had a locked table. Let them know their table changed?`
                  : `${changed} user(s) were moved to a locked table. Send the booking details email so they know where to go?`,
                confirmText: "Send emails",
                cancelText: "Skip",
              });
              if (!confirmed) return;
              sendUserEmails.mutate(
                {
                  eventId: eventTimeId,
                  userIds: changedUserIds,
                  template,
                  dryRun: false,
                },
                {
                  onSuccess: (emailData) => {
                    if (emailData.success) {
                      toast.success(`Emails sent to ${changed} user(s)`);
                    } else {
                      toast.error(emailData.error || "Failed to send emails");
                    }
                  },
                  onError: (error: any) => {
                    toast.error(error?.message || "Failed to send emails");
                  },
                }
              );
            })();
          }
        },
        onError: (error: any) => {
          toast.error(error?.message || "Failed to reassign table");
        },
      }
    );
  };

  if (!isOpen) return null;

  const submitLabel = (() => {
    if (sourceCount === 0) return "Table is empty";
    if (!choice) return "Choose an action";
    if (choice.kind === "unassign") return `Unassign all ${sourceCount}`;
    if (choice.kind === "cant_be_seated") return `Mark all ${sourceCount} can't be seated`;
    return `Move all ${sourceCount}`;
  })();

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={() => (!batchReassign.isPending ? onClose() : undefined)}
    >
      <div
        className="bg-base-100 rounded-xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-base-300">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-lg leading-tight">
                Move entire table
              </h3>
              {isLoading && !sourceTable ? (
                <p className="text-sm text-base-content/60">Loading…</p>
              ) : sourceTable ? (
                <p className="text-sm text-base-content/60 truncate">
                  <span className="font-medium">
                    {sourceTable.reservationName}
                  </span>
                  {sourceTable.restaurantName ? (
                    <span className="text-base-content/40">
                      {" "}
                      · {sourceTable.restaurantName}
                    </span>
                  ) : null}
                </p>
              ) : (
                <p className="text-sm text-error">Table not found</p>
              )}
            </div>
          </div>

          {sourceTable && (
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-base-200">
                <Users className="w-3 h-3" />
                {sourceCount} user(s) on this table
              </span>
              {sourceNeighborhood && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-base-200">
                  <MapPin className="w-3 h-3" />
                  {sourceNeighborhood}
                </span>
              )}
              {sourceTable.isLocked && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/15 text-amber-400">
                  Locked — users were already notified of this table
                </span>
              )}
            </div>
          )}
        </div>

        <div className="p-5 flex-1 overflow-y-auto space-y-3">
          {!sourceTable ? (
            <p className="text-sm text-base-content/60">
              {isLoading ? "Loading table…" : "Could not load table details."}
            </p>
          ) : sourceCount === 0 ? (
            <p className="text-sm text-base-content/60">
              No users are assigned to this table.
            </p>
          ) : (
            <>
              <input
                type="text"
                placeholder="Filter tables…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="input input-bordered input-sm w-full"
              />

              <div className="space-y-1.5">
                <label
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    choice?.kind === "unassign"
                      ? "border-warning bg-warning/5"
                      : "border-base-300 hover:border-base-content/30"
                  }`}
                >
                  <input
                    type="radio"
                    name="table-reassign-choice"
                    checked={choice?.kind === "unassign"}
                    onChange={() => setChoice({ kind: "unassign" })}
                    className="radio radio-warning radio-sm"
                  />
                  <UserMinus className="w-4 h-4 text-warning shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-sm">
                      Unassign all {sourceCount} user(s)
                    </p>
                    <p className="text-xs text-base-content/60 truncate">
                      Free every seat. No emails are sent.
                    </p>
                  </div>
                </label>

                <label
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    choice?.kind === "cant_be_seated"
                      ? "border-error bg-error/5"
                      : "border-base-300 hover:border-base-content/30"
                  }`}
                >
                  <input
                    type="radio"
                    name="table-reassign-choice"
                    checked={choice?.kind === "cant_be_seated"}
                    onChange={() => setChoice({ kind: "cant_be_seated" })}
                    className="radio radio-error radio-sm"
                  />
                  <Ban className="w-4 h-4 text-error shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-sm">
                      Can't be seated: all {sourceCount} user(s)
                    </p>
                    <p className="text-xs text-base-content/60 truncate flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      Sends one apology email · refunds every ticket
                    </p>
                  </div>
                </label>

                <div className="pt-1 text-xs uppercase tracking-wide text-base-content/40">
                  Move everyone to
                </div>

                {filteredTables.length === 0 ? (
                  <p className="text-sm text-base-content/50 text-center py-4">
                    {tables.length <= 1
                      ? "No other tables for this event"
                      : "No tables match the filter"}
                  </p>
                ) : (
                  filteredTables.map((table) => {
                    const isSelected =
                      choice?.kind === "table" && choice.tableId === table.id;
                    const freeSeats = table.capacity - table.currentCount;
                    const wouldOverfill = sourceCount > freeSeats;
                    const isSameNeighborhood =
                      !!sourceNeighborhood &&
                      table.restaurantNeighborhoodName?.toLowerCase() ===
                        sourceNeighborhood.toLowerCase();
                    return (
                      <label
                        key={table.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          isSelected
                            ? "border-primary bg-primary/5"
                            : "border-base-300 hover:border-base-content/30"
                        }`}
                      >
                        <input
                          type="radio"
                          name="table-reassign-choice"
                          checked={isSelected}
                          onChange={() =>
                            setChoice({ kind: "table", tableId: table.id })
                          }
                          className="radio radio-primary radio-sm"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {table.reservationName}
                            {table.isLocked ? (
                              <span className="ml-1.5 text-[10px] uppercase tracking-wide text-base-content/50">
                                locked
                              </span>
                            ) : null}
                          </p>
                          <p className="text-xs text-base-content/60 truncate">
                            {table.restaurantName || "Unknown restaurant"}
                            {table.restaurantNeighborhoodName
                              ? ` · ${table.restaurantNeighborhoodName}`
                              : ""}
                            {isSameNeighborhood ? " · same area" : ""}
                          </p>
                        </div>
                        <div
                          className={`text-xs shrink-0 ${
                            wouldOverfill ? "text-error" : "text-base-content/50"
                          }`}
                        >
                          {table.currentCount}/{table.capacity}
                          {wouldOverfill
                            ? ` · +${sourceCount} overfills`
                            : ""}
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        <div className="p-5 border-t border-base-300 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={batchReassign.isPending}
            className="btn btn-ghost btn-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={
              !choice || batchReassign.isPending || !sourceTable || sourceCount === 0
            }
            className={`btn btn-sm ${
              choice?.kind === "cant_be_seated"
                ? "btn-error"
                : choice?.kind === "unassign"
                  ? "btn-warning"
                  : "btn-primary"
            }`}
          >
            {batchReassign.isPending ? (
              <span className="loading loading-spinner loading-xs" />
            ) : choice?.kind === "cant_be_seated" ? (
              <Ban className="w-4 h-4 mr-1" />
            ) : choice?.kind === "unassign" ? (
              <UserMinus className="w-4 h-4 mr-1" />
            ) : (
              <ArrowRightLeft className="w-4 h-4 mr-1" />
            )}
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
